/**
 * Отрасль на онбординге: список плиток и живая подстановка пресета.
 *
 * ЕДИНСТВЕННОЕ МЕСТО ФРОНТЕНДА, ГДЕ ПРЕСЕТ ЧИТАЕТСЯ ЗАКОННО. Организации ещё нет, значения
 * только предстоит скопировать в неё, и показать их до нажатия «Создать» — прямая обязанность
 * экрана: владелец выбирает не иконку, а шаг сетки, окно отмены, лимиты и срок бана.
 *
 * После создания эти же поля читаются ТОЛЬКО из `CabinetBusinessDto` (AGENTS.md, правило 5):
 * правка пресета не должна менять правила у живых организаций, а владелец мог изменить
 * любое значение вручную.
 *
 * Значения не переписываются здесь списком, а берутся из `INDUSTRY_PRESETS`: копия разошлась
 * бы с пресетами на первой же правке ТЗ §3, и владелец увидел бы на онбординге одни правила,
 * а получил другие.
 */

import type { BusinessType, IndustryPreset } from '@app/shared';
import type { UiKey } from '../../../i18n/dict';
import { ADDRESS_MAX_LENGTH, NAME_MAX_LENGTH, NAME_MIN_LENGTH } from './businessProfile';


/**
 * Значение строки пресета. Разбор по видам, а не готовая строка: единицы измерения живут
 * в словаре («мин», «ч», «дн», «сум/мес»), и склеить их здесь значило бы завести второе
 * место с видимым текстом мимо i18n (ТЗ §14.3).
 */
export type PresetValue =
  | {
      readonly kind: 'minutes' | 'hours' | 'days' | 'count' | 'sumPerMonth';
      readonly amount: number;
    }
  | { readonly kind: 'text'; readonly textKey: UiKey };

export interface PresetRow {
  readonly labelKey: UiKey;
  readonly value: PresetValue;
}

const FREE_TIER_KEYS: Readonly<Record<IndustryPreset['freeTier'], UiKey>> = {
  yes: 'unitYes',
  light: 'unitFreeLight',
  no: 'unitNo',
};

/**
 * Что организация получит от отрасли. Набор и порядок строк повторяют прототип
 * (`renderOnboard`): сначала правила работы, затем деньги, затем название специалиста.
 *
 * Триал, Free-тариф и цены показываются наравне с правилами, хотя в `CabinetBusinessDto`
 * их нет: это часть выбора отрасли, и узнать цену после создания организации владельцу
 * было бы уже нечем (приёма платежей в сборке нет, см. `PlanScreen`).
 */
export function presetRows(preset: IndustryPreset): readonly PresetRow[] {
  return [
    { labelKey: 'onbSlotStep', value: { kind: 'minutes', amount: preset.slotStepMin } },
    { labelKey: 'ruleCancelWindow', value: { kind: 'hours', amount: preset.cancelWindowHours } },
    { labelKey: 'ruleMaxActive', value: { kind: 'count', amount: preset.maxActiveBookings } },
    { labelKey: 'ruleCooldown', value: { kind: 'minutes', amount: preset.cooldownMinutes } },
    { labelKey: 'ruleLocalBan', value: { kind: 'days', amount: preset.localBanDays } },
    { labelKey: 'onbTrial', value: { kind: 'days', amount: preset.trialDays } },
    { labelKey: 'onbFreeTier', value: { kind: 'text', textKey: FREE_TIER_KEYS[preset.freeTier] } },
    // Цены подписки (onbPriceSolo/onbPriceMulti, kind 'sumPerMonth') скрыты до подключения оплаты.
    { labelKey: 'onbTerm', value: { kind: 'text', textKey: preset.specialistTerm } },
  ];
}

/** Подпись `t` в виде функции: так формат значения тестируется без React-дерева. */
export type TranslateFn = (key: UiKey, params?: Record<string, string | number>) => string;

/**
 * Разряды числа через неразрывный пробел: «99 000», а не «99000».
 *
 * Своя функция вместо `toLocaleString`: узбекской локали в среде выполнения может не быть,
 * и результат разошёлся бы между устройствами. Пробел неразрывный, иначе цена ломается
 * на перенос строки посреди числа.
 */
export function groupDigits(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0');
}

/** Значение строки пресета текстом. Единицы измерения — из словаря, а не из литералов. */
/**
 * Число и единица склеены НЕРАЗРЫВНЫМ пробелом (` `): «30 мин» не должно
 * разрываться переносом строки на узкой карточке пресета.
 *
 * Записан escape-последовательностью, а не самим символом: в исходнике U+00A0
 * неотличим от обычного пробела, и следующая правка сломала бы тест сверки,
 * не показав глазу ни одного изменения.
 */
export function presetValueText(value: PresetValue, t: TranslateFn): string {
  switch (value.kind) {
    case 'text':
      return t(value.textKey);
    case 'count':
      return String(value.amount);
    case 'minutes':
      return `${value.amount} ${t('min')}`;
    case 'hours':
      return `${value.amount} ${t('unitHour')}`;
    case 'days':
      return `${value.amount} ${t('unitDay')}`;
    case 'sumPerMonth':
      return `${groupDigits(value.amount)} ${t('unitSumMonth')}`;
  }
}

/** Почему кнопка «Создать» заблокирована. `null` — форма готова к отправке. */
export type OnboardingBlocker = 'noType' | 'nameShort' | 'nameLong' | 'addressLong';

export interface OnboardingDraft {
  readonly name: string;
  readonly type: BusinessType | null;
  readonly address: string;
}

/**
 * Проверка формы онбординга.
 *
 * Отрасль обязательна и не имеет значения по умолчанию: подставить барбершоп «чтобы форма
 * отправилась» значило бы выдать стоматологии двухчасовое окно отмены вместо суточного
 * (AGENTS.md, правило 7) — и владелец узнал бы об этом от клиента, отменившего визит
 * за три часа без последствий.
 *
 * Отрасль проверяется первой: пока её нет, писать владельцу о коротком названии рано —
 * подпись кнопки должна вести его по форме, а не перечислять всё сразу.
 */
export function onboardingBlocker(draft: OnboardingDraft): OnboardingBlocker | null {
  if (draft.type === null) return 'noType';
  const name = draft.name.trim();
  if (name.length < NAME_MIN_LENGTH) return 'nameShort';
  if (name.length > NAME_MAX_LENGTH) return 'nameLong';
  if (draft.address.trim().length > ADDRESS_MAX_LENGTH) return 'addressLong';
  return null;
}
